import { useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    MaterialReactTable,
    useMaterialReactTable
} from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import { useMutation, useQuery } from '@apollo/client';
import {
    GET_INVOICES,
    MODIFY_INVOICE,
} from '../../services/graphql';
import { getTableDensityStyles, normalizeTableDensity } from '../../config/tableDensity';
import { useSelector } from 'react-redux';
import CancelButton from '../CancelButton/CancelButton';
import AssignApproverModal from '../AssignApproverModal/AssignApproverModal';
import useAssignApprover from '../../hooks/useAssignApprover';

const originalColumns = [
    {
        accessorKey: 'date',
        header: 'Date',
        type: "date",
        size: 80,
        dateSetting: { locale: "en-GB" },
    },
    {
        accessorKey: 'invoiceID',
        header: '#',
        size: 50,
    },
    {
        accessorKey: 'supplierEmail',
        header: 'Submitted By',
        size: 180,
    },
    {
        accessorKey: 'expenseType',
        header: 'Expense Type',
        size: 150,
    },
    {
        accessorKey: 'category',
        header: 'Category',
        size: 150,
    },
    {
        accessorKey: 'subCategory',
        header: 'Sub-category',
        size: 150,
    },
    {
        accessorKey: 'originalAmount',
        header: 'Sub Total ($)',
        size: 150,
    },
    {
        accessorKey: 'status',
        header: 'Status',
        size: 100,
    },
    {
        accessorKey: 'paidDate',
        header: 'Remittance Date',
        type: "date",
        size: 100,
        dateSetting: { locale: "en-GB" },
    },
    {
        accessorKey: 'approverEmails',
        header: 'Assigned To',
        size: 150,
    },
    {
        accessorKey: 'GST',
        header: 'GST ($)',
        size: 80,
    },
    {
        accessorKey: 'PST',
        header: 'PST ($)',
        size: 80,
    },
    {
        accessorKey: 'distance',
        header: 'Distance',
        size: 100,
    },
    {
        accessorKey: 'project',
        header: 'Project',
        size: 100,
    },
    {
        accessorKey: 'entity',
        header: 'Entity',
        size: 100,
    },
    {
        accessorKey: 'allocation',
        header: 'Allocation',
        size: 150,
    },
    {
        accessorKey: 'capital',
        header: 'Capital',
        size: 120,
    },
    {
        accessorKey: 'fiscalYear',
        header: 'Fiscal Year',
        size: 120,
    },
    {
        accessorKey: 'GIFICode',
        header: 'GIFI Code',
        size: 120,
    },
    {
        accessorKey: 'expenseGroup',
        header: 'Expense Group',
        size: 150,
    },
    {
        accessorKey: 'backOfficeNote',
        header: 'Back Office Note',
        size: 180,
    },
];

const DashboardTable = ({
    selectedColumns,
    viewBy,
    showColumnFilters,
    globalFilter,
    onGlobalFilterChange,
    columnOrder,
    editControlsTargetId,
    controlButtonClassName,
    cancelButtonClassName,
    controlMetaClassName,
    refreshSignal = 0,
}) => {
    const navigate = useNavigate();
    const accessLevel = useSelector((state) => state.graphql.accessLevel);
    const isManager = accessLevel === 'Manager';
    const canEditFinancialFields = accessLevel === 'Manager' || accessLevel?.startsWith('Approver');
    const editableFields = new Set(['GIFICode', 'fiscalYear', 'backOfficeNote', 'expenseGroup', 'capital']);
    const [data, setData] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 5,
    });
    const [rowCount, setRowCount] = useState();
    const tableDensity = normalizeTableDensity(localStorage.getItem('tableDensity'));
    const densityStyles = getTableDensityStyles(tableDensity);
    const [isEditingGrid, setIsEditingGrid] = useState(false);
    const [originalRowsById, setOriginalRowsById] = useState({});
    const [dirtyCellsByInvoiceId, setDirtyCellsByInvoiceId] = useState({});
    const [isSavingGrid, setIsSavingGrid] = useState(false);
    const [fillDrag, setFillDrag] = useState({ active: false, field: null, value: '' });

    const isDarkMode = useSelector((state) => state.graphql.darkMode);
    const dirtyCellCount = Object.values(dirtyCellsByInvoiceId).reduce((sum, row) => sum + Object.keys(row).length, 0);
    const hasUnsavedChanges = dirtyCellCount > 0;
    const [modifyInvoiceMutation] = useMutation(MODIFY_INVOICE, {
        onError: (error) => {
            console.error('Error saving dashboard edits:', error);
        },
    });
    const {
        assigningInvoiceID,
        selectedApproverEmail,
        setSelectedApproverEmail,
        availableApprovers,
        assignApproverLoading,
        approversLoading,
        openAssignApproverModal,
        closeAssignApproverModal,
        assignSelectedApprover,
    } = useAssignApprover({
        enabled: isManager,
    });

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (!hasUnsavedChanges) return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    useEffect(() => {
        const handleMouseUp = () => {
            if (fillDrag.active) {
                setFillDrag({ active: false, field: null, value: '' });
            }
        };
        window.addEventListener('mouseup', handleMouseUp);
        return () => window.removeEventListener('mouseup', handleMouseUp);
    }, [fillDrag.active]);

    const snapshotRowsById = (rows) => {
        const snapshot = {};
        rows.forEach((row) => {
            snapshot[row.invoiceID] = { ...row };
        });
        return snapshot;
    };

    const normalizeValue = (value) => (value == null ? '' : String(value).trim());
    const toBooleanLike = (value) => {
        if (value === true) return true;
        if (typeof value === 'number') return value > 0;
        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            if (normalized === 'true') return true;
            const numeric = Number(normalized);
            return Number.isFinite(numeric) && numeric > 0;
        }
        return false;
    };
    const parseApproverEmails = (value) =>
        String(value || '')
            .split(',')
            .map((email) => email.trim())
            .filter(Boolean);

    const isInvoiceUnassigned = (invoice) => {
        const hasNoApproverFlag = toBooleanLike(invoice?.noApproverAvailable);
        const hasApprovers = parseApproverEmails(invoice?.approverEmails).length > 0;
        return hasNoApproverFlag || !hasApprovers;
    };

    const handleAssignApprover = async () => {
        await assignSelectedApprover(({ invoiceID, approverEmail }) => {
            setData((prev) =>
                prev.map((invoice) => {
                    if (String(invoice.invoiceID) !== String(invoiceID)) return invoice;
                    const approvers = parseApproverEmails(invoice.approverEmails);
                    const hadPriorApprover = approvers.length > 0;
                    if (!approvers.includes(approverEmail)) {
                        approvers.push(approverEmail);
                    }
                    const nextStatus = hadPriorApprover ? 'Processing' : 'Pending';
                    return {
                        ...invoice,
                        approverEmails: approvers.join(','),
                        noApproverAvailable: false,
                        status: nextStatus,
                    };
                })
            );
            refetch();
        });
    };

    const updateDirtyState = (invoiceID, field, value, originalMap) => {
        setDirtyCellsByInvoiceId((prev) => {
            const next = { ...prev };
            const originalValue = normalizeValue(originalMap[invoiceID]?.[field]);
            const currentValue = normalizeValue(value);

            if (currentValue === originalValue) {
                if (next[invoiceID]) {
                    const rowChanges = { ...next[invoiceID] };
                    delete rowChanges[field];
                    if (Object.keys(rowChanges).length === 0) {
                        delete next[invoiceID];
                    } else {
                        next[invoiceID] = rowChanges;
                    }
                }
                return next;
            }

            next[invoiceID] = { ...(next[invoiceID] || {}), [field]: value };
            return next;
        });
    };

    const applyCellValue = (invoiceID, field, value) => {
        setData((prev) =>
            prev.map((row) =>
                row.invoiceID === invoiceID
                    ? { ...row, [field]: value }
                    : row
            )
        );
        updateDirtyState(invoiceID, field, value, originalRowsById);
    };

    const handleEnableEditMode = () => {
        setOriginalRowsById(snapshotRowsById(data));
        setDirtyCellsByInvoiceId({});
        setIsEditingGrid(true);
    };

    const handleCancelEdits = () => {
        setData((prev) =>
            prev.map((row) => (originalRowsById[row.invoiceID] ? { ...originalRowsById[row.invoiceID] } : row))
        );
        setDirtyCellsByInvoiceId({});
        setIsEditingGrid(false);
        setFillDrag({ active: false, field: null, value: '' });
    };

    const handleSaveEdits = async () => {
        if (!hasUnsavedChanges) {
            setIsEditingGrid(false);
            return;
        }

        setIsSavingGrid(true);
        try {
            const invoiceIDs = Object.keys(dirtyCellsByInvoiceId);
            const savePromises = invoiceIDs.map((invoiceID) => {
                const changedFields = dirtyCellsByInvoiceId[invoiceID] || {};
                const currentRow = data.find((row) => String(row.invoiceID) === String(invoiceID));
                const oldRow = originalRowsById[invoiceID];
                if (!currentRow || !oldRow) return Promise.resolve();

                const oldValues = {};
                Object.keys(changedFields).forEach((field) => {
                    oldValues[field] = oldRow[field] ?? null;
                });

                const variables = {
                    invoiceID: String(invoiceID),
                    originalAmount: String(currentRow.originalAmount ?? oldRow.originalAmount ?? '0'),
                    currency: String(currentRow.currency ?? oldRow.currency ?? 'CAD'),
                    CADAmount: currentRow.CADAmount != null ? String(currentRow.CADAmount) : String(oldRow.CADAmount ?? currentRow.originalAmount ?? '0'),
                    oldValues: JSON.stringify(oldValues),
                    ...changedFields,
                };

                return modifyInvoiceMutation({ variables });
            });

            await Promise.all(savePromises);
            setOriginalRowsById(snapshotRowsById(data));
            setDirtyCellsByInvoiceId({});
            setIsEditingGrid(false);
            setFillDrag({ active: false, field: null, value: '' });
        } catch (error) {
            console.error('Failed to save dashboard edits:', error);
            alert('Failed to save one or more edits. Please try again.');
        } finally {
            setIsSavingGrid(false);
        }
    };

    const columns = useMemo(() => {
        const visibleColumns = originalColumns.filter((column) =>
            selectedColumns.includes(column.accessorKey)
        );
        const orderSource = (Array.isArray(columnOrder) && columnOrder.length > 0)
            ? columnOrder
            : selectedColumns;
        const orderIndex = new Map(orderSource.map((key, index) => [key, index]));
        visibleColumns.sort(
            (a, b) =>
                (orderIndex.get(a.accessorKey) ?? Number.MAX_SAFE_INTEGER) -
                (orderIndex.get(b.accessorKey) ?? Number.MAX_SAFE_INTEGER)
        );
        return visibleColumns.map((column) => {
            if (!editableFields.has(column.accessorKey)) {
                return column;
            }
            return {
                ...column,
                Cell: ({ row }) => {
                    const invoiceID = row.original.invoiceID;
                    const field = column.accessorKey;
                    const value = row.original[field] ?? '';

                    if (!isEditingGrid || !canEditFinancialFields) {
                        return <span>{value}</span>;
                    }

                    return (
                        <div
                            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                            onMouseEnter={() => {
                                if (fillDrag.active && fillDrag.field === field) {
                                    applyCellValue(invoiceID, field, fillDrag.value);
                                }
                            }}
                        >
                            <input
                                value={value}
                                onChange={(event) => applyCellValue(invoiceID, field, event.target.value)}
                                style={{
                                    width: '100%',
                                    minWidth: 80,
                                    border: '1px solid var(--theme-border)',
                                    borderRadius: 6,
                                    padding: '2px 6px',
                                    fontSize: '12px',
                                    background: 'var(--theme-bg-input)',
                                    color: 'var(--theme-text)',
                                }}
                            />
                            <span
                                title="Drag to fill down"
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    setFillDrag({ active: true, field, value });
                                }}
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: 2,
                                    background: '#355c9b',
                                    cursor: 'crosshair',
                                    flexShrink: 0,
                                }}
                            />
                        </div>
                    );
                },
            };
        });
    }, [selectedColumns, columnOrder, isEditingGrid, canEditFinancialFields, fillDrag]);


    const customIcons = {
        SearchIcon: () => <SearchIcon color='primary' />,
        SearchOffIcon: () => <SearchIcon color='primary' />,
        FilterListIcon: () => <TuneIcon color='primary' />,
    };
    function convertAndStringifyFilters(inputArray) {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        let currentMonth = currentDate.getMonth() + 1;
        const currentDay = currentDate.getDate();
        let andFilter = [{}];

        if (viewBy == 'currentYear') {
            andFilter[0].field = 'date';
            andFilter[0].operation = 'contains';
            andFilter[0].value = currentYear.toString();
        } else if (viewBy == 'currentMonth') {
            if (currentMonth < 10) {
                currentMonth = '0' + currentMonth;
            }
            currentMonth = currentYear + '-' + currentMonth + '-';
            andFilter[0].field = 'date';
            andFilter[0].operation = 'contains';
            andFilter[0].value = currentMonth;
        } else if (viewBy == 'currentPeriod') {
            // current period starts in this month
            if (currentDay >= 15) {
                if (currentMonth < 10) {
                    currentMonth = '0' + currentMonth;
                }
                andFilter[0].field = 'date';
                andFilter[0].operation = 'greater_than_or_equals';
                andFilter[0].value = currentYear + '-' + currentMonth + '-' + '15';
            }
            // current period started previous month
            else {
                let prevMonth = currentMonth - 1;
                if (prevMonth < 10) {
                    prevMonth = '0' + prevMonth;
                }
                andFilter[0].field = 'date';
                andFilter[0].operation = 'greater_than_or_equals';
                andFilter[0].value = currentYear + '-' + prevMonth + '-' + '15';
            }
        }

        if (columnFilters.length > 0) {
            const convertedArray = inputArray.map(inputObject => {
                return {
                    field: inputObject.id,
                    operation: "contains",
                    value: inputObject.value
                };
            });
            andFilter = andFilter.concat(convertedArray);
        }

        return JSON.stringify(andFilter);
    }

    function convertAndStringifyGlobalFilter(globalFilter) {
        // or filter for all selected columns
        const convertedArray = columns.map(column => {
            return {
                field: column.accessorKey,
                operation: "contains",
                value: globalFilter,
            };
        });

        return JSON.stringify(convertedArray);
    }

    const { loading, error, data1, refetch } = useQuery(GET_INVOICES);
    if (error) {
        alert('Unable to load invoices.');
    }

    const extractInvoices = (payload) => {
        const body = payload?.data?.getInvoices?.body;
        return Array.isArray(body) ? body : [];
    };

    useEffect(() => {
        let variables = {
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    sortBy: sorting.length > 0 ? sorting[0].id : null,
            sortOrder: sorting.length > 0 ? sorting[0].desc : null,
    filter: {
        andFilter: null,
        orFilter: null,
    }
};

        // to obtain the total number of invoices with the current filter
        let variablesTotal = {
            page: 1,
            pageSize: Infinity,
            filter: {
                andFilter: null,
                orFilter: null,
            }
        };

        const andFilter = convertAndStringifyFilters(columnFilters);
        const orFilter = globalFilter && convertAndStringifyGlobalFilter(globalFilter);

        if (andFilter) { 
            variables.filter.andFilter = andFilter; 
            variablesTotal.filter.andFilter = andFilter;
        }
        else { 
            variables.filter.andFilter = null; 
            variablesTotal.filter.andFilter = null;
        }
        
        if (orFilter) { 
            variables.filter.orFilter = orFilter; 
            variablesTotal.filter.orFilter = orFilter; 
        }
        else { 
            variables.filter.orFilter = null; 
            variablesTotal.filter.orFilter = null; 
        }

        // to get the total # of invoices with the set filtering, and set row count for correct front-end pagination
        refetch(variablesTotal)
            .then((payload) => {
                const invoices = extractInvoices(payload);
                setRowCount(invoices.length);
            })
            .catch(error => {
                console.error('Error during refetch:', error);
                setRowCount(0);
            });

        // to get invoices on the current page
        refetch(variables)
            .then((payload) => {
                const invoices = extractInvoices(payload);
                setData(invoices);
            })
            .catch(error => {
                console.error('Error during refetch:', error);
                setData([]);
            });
    }, [pagination, columnFilters, sorting, globalFilter, viewBy, refreshSignal]);

    const table = useMaterialReactTable({
        columns,
        data,
        initialState: { density: densityStyles.density, showColumnFilters: false },
        muiTableHeadRowProps: {
            sx: { backgroundColor: 'var(--theme-bg-elevated)' },
        },
        muiTableHeadCellProps: ({ table }) => ({
            sx: table.getState().density === 'compact'
                ? densityStyles.headCell
                : {},
        }),
        muiTableBodyCellProps: ({ table }) => ({
            sx: table.getState().density === 'compact'
                ? densityStyles.bodyCell
                : {},
        }),
        mrtTheme: {
            baseBackgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
        },
        muiTablePaperProps: {
            sx: { backgroundColor: 'var(--theme-bg-elevated)' },
        },
        muiBottomToolbarProps: {
            sx: { backgroundColor: 'var(--theme-bg-elevated)', color: 'var(--theme-text)' },
        },
        enableTopToolbar: false,
        enableColumnResizing: true,
        icons: customIcons,
        muiPaginationProps: {
            color: 'primary',
            shape: 'rounded',
            showRowsPerPage: true,
            variant: 'outlined',
        },
        paginationDisplayMode: 'pages',
        enableHiding: false,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        enableRowActions: isManager,
        positionActionsColumn: 'last',
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        rowCount, //update to fetched one later
        onGlobalFilterChange: onGlobalFilterChange,
        onColumnFiltersChange: setColumnFilters,
        onPaginationChange: setPagination,
        //onSortingChange: setSorting,
        state: {
            columnFilters,
            pagination,
            sorting,
            globalFilter,
            showColumnFilters,
            showSkeletons: rowCount === undefined,
            columnOrder: columns.map((column) => column.accessorKey),
        },
        onSortingChange: setSorting,
        renderRowActions: ({ row }) => {
            if (!isManager || !isInvoiceUnassigned(row.original)) {
                return null;
            }
            return (
                <button
                    onClick={(event) => {
                        event.stopPropagation();
                        openAssignApproverModal(row.original?.invoiceID);
                    }}
                    style={{
                        border: 'none',
                        borderRadius: 6,
                        background: '#153243',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '4px 8px',
                        cursor: 'pointer',
                    }}
                >
                    Assign
                </button>
            );
        },
        muiTableBodyRowProps: ({ row }) => {
    //console.log('Row data:', row.original);

    const id = row.original['invoiceID'];
    const category = row.original['category'];

    return {
        onDoubleClick: () => {
            if (hasUnsavedChanges) {
                const proceed = window.confirm('You have unsaved dashboard edits. Continue without saving?');
                if (!proceed) return;
            }
            sessionStorage.setItem("selectedInvoiceID", id);
            navigate(`/details/${category}/${id}`);
        },
        sx: {
            cursor: 'pointer',
            backgroundColor: 'var(--theme-bg-elevated)',
            ...densityStyles.bodyRow,
        },
        onTouchEnd: () => {
            if (hasUnsavedChanges) {
                const proceed = window.confirm('You have unsaved dashboard edits. Continue without saving?');
                if (!proceed) return;
            }
            sessionStorage.setItem("selectedInvoiceID", id);
            navigate(`/details/${category}/${id}`);
        },
    };
},
    });

    const renderEditControls = () => {
        if (!canEditFinancialFields) return null;
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {!isEditingGrid ? (
                    <button onClick={handleEnableEditMode} className={controlButtonClassName}>
                        Enable Editing
                    </button>
                ) : (
                    <>
                        <button
                            onClick={handleSaveEdits}
                            disabled={isSavingGrid || !hasUnsavedChanges}
                            className={controlButtonClassName}
                        >
                            {isSavingGrid ? 'Saving...' : 'Save'}
                        </button>
                        <CancelButton
                            label="Cancel"
                            onClick={handleCancelEdits}
                            useContainer={false}
                            className={cancelButtonClassName}
                            disabled={isSavingGrid}
                        />
                        <span className={controlMetaClassName}>
                            {hasUnsavedChanges ? `${dirtyCellCount} unsaved change(s)` : 'No changes yet'}
                        </span>
                    </>
                )}
            </div>
        );
    };

    const editControlsTarget =
        typeof document !== 'undefined' && editControlsTargetId
            ? document.getElementById(editControlsTargetId)
            : null;

    return (
        <>
            <AssignApproverModal
                isOpen={Boolean(assigningInvoiceID)}
                invoiceID={assigningInvoiceID}
                selectedApproverEmail={selectedApproverEmail}
                onApproverChange={setSelectedApproverEmail}
                availableApprovers={availableApprovers}
                onCancel={closeAssignApproverModal}
                onAssign={handleAssignApprover}
                isAssigning={assignApproverLoading}
                isApproversLoading={approversLoading}
                cancelButtonStyle={{
                    border: 'none',
                    borderRadius: 6,
                    background: '#153243',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '6px 10px',
                    cursor: 'pointer',
                }}
                assignButtonStyle={{
                    border: 'none',
                    borderRadius: 6,
                    background: '#153243',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '6px 10px',
                    cursor: 'pointer',
                }}
            />
            {editControlsTarget
                ? createPortal(renderEditControls(), editControlsTarget)
                : null}
            {!editControlsTarget ? renderEditControls() : null}
            <MaterialReactTable
                table={table}
            />
        </>
    );
};

export default DashboardTable;
